import type { CellRenderer } from './StickyTable';

interface ScrollbarVisibilityInput {
  totalWidth: number;
  totalHeight: number;
  width: number;
  height: number;
  scrollbarSizes: { width: number; height: number };
  fillContainer?: boolean;
}

export const getScrollbarVisibility = ({
  totalWidth,
  totalHeight,
  width,
  height,
  scrollbarSizes,
  fillContainer = false
}: ScrollbarVisibilityInput): { hasHorizScroll: boolean; hasVertScroll: boolean } => {
  let hasHorizScroll = totalWidth > width;
  let hasVertScroll = totalHeight > height;
  // A fill-container view grows by each scrollbar's size and the outer wrapper clips the bars,
  // so one bar never takes room from the other axis.
  if (fillContainer) {
    return { hasHorizScroll, hasVertScroll };
  }
  if (hasHorizScroll) {
    hasVertScroll = totalHeight + scrollbarSizes.height > height;
  }
  if (hasVertScroll) {
    hasHorizScroll = totalWidth + scrollbarSizes.width > width;
  }
  return { hasHorizScroll, hasVertScroll };
};

interface FilledContentWidthInput {
  contentWidth: number;
  contentHeight: number;
  availableWidth: number;
  availableHeight: number;
  scrollbarWidth: number;
  fillContainer?: boolean;
}

// Widens content narrower than the view to fill it. Room for a vertical scrollbar is left only when
// there will be one: an auto-height table has none, and narrowing it anyway makes it scroll sideways.
export const getFilledContentWidth = ({
  contentWidth,
  contentHeight,
  availableWidth,
  availableHeight,
  scrollbarWidth,
  fillContainer = false
}: FilledContentWidthInput): number => {
  if (contentWidth >= availableWidth) {
    return contentWidth;
  }
  const hasVertScrollbar = fillContainer || contentHeight > availableHeight;
  return availableWidth - (hasVertScrollbar ? scrollbarWidth : 0);
};

// Adds a column's Header.align class (st-align-*) to every cell a renderer draws. A column
// without align passes through untouched, so tables that don't opt in render as before.
export const withColumnAlign =
  <T>(
    renderer: CellRenderer<T>,
    getAlign: (columnIndex: number) => string | undefined
  ): CellRenderer<T> =>
  args => {
    const align = getAlign(args.columnIndex);
    return renderer(align ? { ...args, classes: [...args.classes, `st-align-${align}`] } : args);
  };
